/**
 * Shower systems — the prefab showers a contractor sells, one maker's base,
 * wall panels, niche and accessories per shower, in that maker's colours
 * (claude-docs/2026-10-28-shower-systems-design.md, issue #2960).
 *
 * A shower carries its choice as `showerSystem: { id, colour, finish }` on
 * its object (the bathtub's, for a tub/shower). Which systems a company sells
 * is a price-free `shower-system--<id>` row in its price book with
 * `offered: true` — and, unlike every other offering row, nothing is offered
 * until a row says so: a company sees only the standard tiled shower until it
 * states which systems it offers (Yu-Han, 2026-10-28).
 *
 * `maker` is the brand a price-book line narrows by (`bundleRule` `brand`)
 * and the `manufacturer` the system's textures carry, so a system shower's
 * walls price under its maker. Each colour × wall finish is one texture in
 * `unifiedTextures`, named by `systemTextureId`.
 *
 * Pure: no I/O.
 */

const { wallFaceSpanY } = require('../geometry/wallFace');
const { showerTrimRuns, showerTrimFeet } = require('./trim');

const SHOWER_SYSTEM_KEY_PREFIX = 'shower-system--';

const SHOWER_SYSTEMS = require('./definitions');

/**
 * The types an accessory in a shower is (step 3a): the take-off map's
 * `shower-shelf`, `corner-caddy` and `grab-bar` (#2869) and `shower-seat`.
 * A system's parts carry `showerSystemPart`; a grab bar is the standard one,
 * on any shower, of no system.
 */
const ACCESSORY_TYPES = new Set([
  'shower-shelf',
  'corner-caddy',
  'shower-seat',
  'grab-bar',
]);

/**
 * The standard grab bar (step 3a, any shower; my call — Onyx publishes no
 * size for one): a 24" stainless bar of 1¼" tube standing 1½" off the wall,
 * its centre 36" above the floor (the top of the usual 33–36" band), with a
 * 3" round flange at each end.
 */
const STANDARD_GRAB_BAR = Object.freeze({
  lengthIn: 24,
  diameterIn: 1.25,
  standoffIn: 1.5,
  centreIn: 36,
  flangeIn: 3,
  hex: '#C9C9C9',
});

/**
 * A plain prefab pan (a tile shower set to Prefab, no system): drawn white
 * on a low curb.
 */
const PLAIN_PAN = Object.freeze({
  hex: '#F4F3EF',
  curb: Object.freeze({ heightIn: 2, widthIn: 3.5 }),
});

// a size within this of a standard one is that size
const STANDARD_SIZE_TOLERANCE_IN = 0.5;

const showerSystemById = (id) =>
  SHOWER_SYSTEMS.find((system) => system.id === id) ?? null;

/**
 * Can a system be sold yet (step 4a)? Only once it has colours and wall
 * finishes — the textures its walls are drawn in. A maker known by name only
 * is never offered, chosen or switched on by an import.
 */
const isSystemReady = (system) =>
  (system?.colours?.length ?? 0) > 0 && (system?.wallFinishes?.length ?? 0) > 0;

/** The systems a company can offer today. */
const READY_SHOWER_SYSTEMS = Object.freeze(
  SHOWER_SYSTEMS.filter(isSystemReady)
);

/**
 * The wall finishes a colour of `system` is made in, in the system's order:
 * those its `finishes` lists, or every one where it lists none (Onyx's);
 * [] for a colour the system does not have (step 4b).
 */
function colourFinishes(system, colourId) {
  const c = (system?.colours || []).find((x) => x.id === colourId);
  if (!c) return [];
  const all = system.wallFinishes || [];
  return c.finishes ? all.filter((f) => c.finishes.includes(f.id)) : all;
}

/**
 * `choice` in another colour: its finish kept where that colour is made in
 * it, else the colour's first — so a colour pick never lands on a panel that
 * does not exist (step 4b).
 */
function choiceForColour(choice, colourId) {
  const finishes = colourFinishes(showerSystemById(choice?.id), colourId);
  const keeps = finishes.some((f) => f.id === choice.finish);
  return {
    ...choice,
    colour: colourId,
    finish: keeps ? choice.finish : (finishes[0]?.id ?? choice.finish),
  };
}

/** The price-book key that says whether a company offers a system. */
const showerSystemOfferingKey = (id) => `${SHOWER_SYSTEM_KEY_PREFIX}${id}`;

/**
 * Is `choice` ({ id, colour, finish, curb?, niche? }) one its system's
 * definition names? `curb` and `niche` are optional (a step-1 choice has
 * neither); `niche: null` is the shower's own site-built niche.
 */
function isValidSystemChoice(choice) {
  return !!choice && systemChoiceProblem(choice) === null;
}

/**
 * What is wrong with a system choice, in words for the person who made it,
 * or null when it is valid. A curb left out or null is the system's default
 * (a tub/shower has no base); a niche null is the shower's own site-built
 * one.
 */
function systemChoiceProblem(choice) {
  const system = showerSystemById(choice?.id);
  if (!system) return `There is no shower system "${choice?.id}"`;
  const colourOf = system.colours.find((c) => c.id === choice.colour);
  if (!colourOf) {
    return `${system.name} has no colour "${choice.colour}"`;
  }
  const finish = system.wallFinishes.find((f) => f.id === choice.finish);
  if (!finish) {
    return `${system.name} has no wall finish "${choice.finish}"`;
  }
  if (!colourFinishes(system, colourOf.id).includes(finish)) {
    return `${system.name} has no ${colourOf.name} in ${finish.name}`;
  }
  if (
    choice.curb != null &&
    !(system.curbs || []).some((c) => c.id === choice.curb)
  ) {
    return `${system.name} has no curb "${choice.curb}"`;
  }
  if (
    choice.niche != null &&
    !(system.niches || []).some((n) => n.id === choice.niche)
  ) {
    return `${system.name} has no niche "${choice.niche}"`;
  }
  return trimsProblem(system, choice.trims);
}

/** What is wrong with a choice's `trims`, or null (none is fine). */
function trimsProblem(system, trims) {
  if (trims == null) return null;
  if (!Array.isArray(trims)) return `${system.name}'s trims must be a list`;
  const seen = new Set();
  for (const id of trims) {
    if (!(system.trims || []).some((t) => t.id === id)) {
      return `${system.name} has no trim "${id}"`;
    }
    if (seen.has(id)) return `The trim "${id}" is chosen twice`;
    seen.add(id);
  }
  return null;
}

/** A system's trims sold by the foot (step 3b), or []. */
const systemTrims = (systemId) => showerSystemById(systemId)?.trims || [];

/**
 * The trim around a WINDOW inside a system shower's panels (2026-10-31):
 * no system's choice names it — any window the panels reach is trimmed —
 * so it is no entry of a system's `trims`, but it is measured and priced
 * the same way, as the window piece of solid-surface trim.
 */
const WINDOW_TRIM = Object.freeze({
  id: 'window-trim',
  name: 'Window Trim',
  piece: 'window',
});

/** …and the cap on its sill, for a system whose window takes one. */
const WINDOW_SILL_CAP = Object.freeze({
  id: 'window-sill',
  name: 'Window Sill Cap',
  piece: 'cap',
});

/**
 * How a window inside a system's panels is trimmed, `{ sill: 'trim' | 'cap' }`
 * — trim all round, or a cap on the sill and trim on the other three sides —
 * or null for a system whose windows take no trim (Wetwall's).
 */
const systemWindowTrim = (systemId) =>
  showerSystemById(systemId)?.windowTrim ?? null;

/** The part id a system's trim row carries (`products` on a trim bundle). */
const systemTrimPartId = (systemId, trimId) => `${systemId}--${trimId}`;

/** A system's seam bead, `{ radiusIn }`, or null. */
const systemBead = (systemId) => showerSystemById(systemId)?.bead ?? null;

/**
 * The trims a choice names, in the system's own order; [] for no choice, no
 * trims or one its system does not have.
 */
function chosenTrims(choice) {
  const ids = new Set(Array.isArray(choice?.trims) ? choice.trims : []);
  return systemTrims(choice?.id).filter((t) => ids.has(t.id));
}

/**
 * A system's first colour, first wall finish and default curb, or null for
 * no system. `niche` is left unset: the tool turns the shower's own niche
 * into the system niche nearest its size on a first pick.
 */
function defaultSystemChoice(id) {
  const system = showerSystemById(id);
  if (!isSystemReady(system)) return null;
  const first = system.colours[0];
  return {
    id: system.id,
    colour: first.id,
    finish: colourFinishes(system, first.id)[0].id,
    ...(system.defaultCurb ? { curb: system.defaultCurb } : {}),
  };
}

/**
 * Does a system make a base of its own (it lists curbs)? One that does not
 * (Wetwall) stands on the plain prefab pan (step 4b, ruling A).
 */
const systemHasBase = (systemId) =>
  (showerSystemById(systemId)?.curbs?.length ?? 0) > 0;

/**
 * The colour a system's niche and accessories are drawn in: its one
 * `partColour` where it makes them in a fixed colour (Wetwall's Designer
 * White), else the choice's colour (`systemColourHex`); null for no system.
 */
function systemPartColourHex(choice) {
  const fixed = showerSystemById(choice?.id)?.partColour?.hex;
  return fixed ?? systemColourHex(choice);
}

/** Is a shower a neo-angle — two walls, its door on the diagonal? */
const isNeoAngleShower = (obj) =>
  obj?.wallCount === 2 && obj?.doorType === 'diagonal';

/**
 * A system shower's pan against its maker's standard sizes (step 4b):
 * 'custom' where its own footprint is none of them (a neo-angle against its
 * N × N sizes), else null — and null for a shower on no system, or on one
 * with no base of its own.
 */
function systemPanSizing(obj) {
  const choice = obj?.showerSystem;
  if (!isValidSystemChoice(choice) || !systemHasBase(choice.id)) return null;
  const widthIn = (obj.dimensions?.x ?? 0) / 0.0254;
  const depthIn = (obj.dimensions?.z ?? 0) / 0.0254;
  if (!(widthIn > 0 && depthIn > 0)) return null;
  return standardBaseSize(choice, widthIn, depthIn, {
    diagonal: isNeoAngleShower(obj),
  })
    ? null
    : 'custom';
}

/** The solid colour of a choice's colour (`#RRGGBB`), or null. */
function systemColourHex(choice) {
  const system = showerSystemById(choice?.id);
  return system?.colours.find((c) => c.id === choice.colour)?.hex ?? null;
}

/**
 * How thick a choice's wall panels are, inches, as its maker publishes it —
 * recorded, not drawn: the panels lie flat on the wall as tile does (the
 * user, 2026-10-30). A colour of another material says its own (null where
 * the maker publishes none: Sentrel's acrylic Dimensions SimTile), else the
 * system's; null on no system.
 */
function systemPanelThicknessIn(choice) {
  const system = showerSystemById(choice?.id);
  if (!system) return null;
  const colour = system.colours.find((c) => c.id === choice.colour);
  if (colour && 'panelThicknessIn' in colour) return colour.panelThicknessIn;
  return system.panelThicknessIn ?? null;
}

/** A choice's curb (the system's default when it names none), or null. */
function systemCurb(choice) {
  const system = showerSystemById(choice?.id);
  const curb = (system?.curbs || []).find(
    (c) => c.id === (choice.curb ?? system.defaultCurb)
  );
  return curb ? { heightIn: curb.heightIn, widthIn: curb.widthIn } : null;
}

const within = (a, b) => Math.abs(a - b) <= STANDARD_SIZE_TOLERANCE_IN;

/**
 * The maker's standard base a shower `widthIn` × `depthIn` is, on the
 * choice's curb — either way round, within ½" — or null for a custom base.
 * A neo-angle (`diagonal`) is N × N from `neoAngleSizes`.
 */
function standardBaseSize(choice, widthIn, depthIn, { diagonal = false } = {}) {
  const system = showerSystemById(choice?.id);
  if (!system) return null;
  if (diagonal) {
    const n = (system.neoAngleSizes || []).find(
      (size) => within(widthIn, size) && within(depthIn, size)
    );
    return n == null ? null : { widthIn: n, depthIn: n };
  }
  const sizes = (system.baseSizes || {})[choice.curb ?? system.defaultCurb];
  const match = (sizes || []).find(
    ([w, d]) =>
      (within(widthIn, w) && within(depthIn, d)) ||
      (within(widthIn, d) && within(depthIn, w))
  );
  return match ? { widthIn: match[0], depthIn: match[1] } : null;
}

/** The system niche a choice names, or null (none, or the site-built one). */
function systemNiche(choice) {
  if (choice?.niche == null) return null;
  const system = showerSystemById(choice.id);
  return (system?.niches || []).find((n) => n.id === choice.niche) ?? null;
}

/**
 * The opening a system insert sits in — its maker's cut-out hole, which the
 * insert's flange lies around — from the niche's `nicheModel`
 * (`<system>--<niche>`, `systemNicheModelId`); null for anything else.
 *
 * @returns {{widthIn: number, heightIn: number}|null}
 */
function systemNicheOpeningIn(nicheModel) {
  for (const system of SHOWER_SYSTEMS) {
    for (const niche of system.niches || []) {
      if (systemNicheModelId(system.id, niche.id) !== nicheModel) continue;
      return niche.holeWidthIn && niche.holeHeightIn
        ? { widthIn: niche.holeWidthIn, heightIn: niche.holeHeightIn }
        : null;
    }
  }
  return null;
}

/**
 * The system a contractor's price line names (step 5): by its full name
 * (`linePattern`) anywhere, or — `part: true`, a line already read as a
 * shower system's part — by its bare name (`partPattern`). Null for none.
 */
// a stone, a colour or a tile beside a maker's bare name says it is no
// maker: "Black Onyx Quartz", an onyx mosaic, "Onyx Tile Edge Trim"
const STONE_WORDS =
  /\b(?:black|white|quartz|marble|mosaics?|tiles?|granite|stone|slabs?|countertops?)\b/i;
const systemNamedByLine = (name, { part = false } = {}) => {
  const text = name || '';
  return (
    SHOWER_SYSTEMS.find((system) => system.linePattern?.test(text)) ??
    (part && !STONE_WORDS.test(text)
      ? SHOWER_SYSTEMS.find((system) => system.partPattern?.test(text))
      : null) ??
    null
  );
};

// a word of a part's name a line need not say: its series, "shower"
const PART_NAME_FILLER = new Set(['shower', 'with', 'and', 'the']);
const singularWord = (w) =>
  w.length > 3 && w.endsWith('s') && !w.endsWith('ss') ? w.slice(0, -1) : w;
const nameWords = (text) =>
  new Set(
    String(text || '')
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter(Boolean)
      .map(singularWord)
  );

/**
 * The parts of `system` of `type` — its niches for 'shower-niche', else its
 * accessories of that type — a line's name says (step 5), as part ids
 * (`systemNicheModelId`, `systemAccessoryPartId`). A part is said when every
 * word of its name or one of its `aliases` is in the line, less its series
 * and filler; of those, only the ones saying the most words ("Liberty Tall
 * Corner Caddy" is the tall caddy, not both). [] for none.
 */
function systemPartsNamedByLine(system, name, type) {
  if (!system) return [];
  const line = nameWords(name);
  const skip = new Set([...nameWords(system.series), ...PART_NAME_FILLER]);
  const candidates =
    type === 'shower-niche'
      ? (system.niches || []).map((n) => [
          systemNicheModelId(system.id, n.id),
          n,
        ])
      : type === 'shower-bench'
        ? system.bench
          ? [[systemBenchPartId(system), system.bench]]
          : []
        : (system.accessories || [])
            .filter((a) => a.type === type)
            .map((a) => [systemAccessoryPartId(system.id, a.id), a]);
  const said = candidates
    .map(([id, part]) => {
      const scores = [part.name, ...(part.aliases || [])].map((n) => {
        const words = [...nameWords(n)].filter((w) => !skip.has(w));
        return words.length > 0 && words.every((w) => line.has(w))
          ? words.length
          : 0;
      });
      return [id, Math.max(...scores)];
    })
    .filter(([, score]) => score > 0);
  const best = Math.max(0, ...said.map(([, score]) => score));
  return said.filter(([, score]) => score === best).map(([id]) => id);
}

/** The system and niche a niche's `nicheModel` names, or null. */
function systemNicheOfModel(nicheModel) {
  for (const system of SHOWER_SYSTEMS) {
    for (const niche of system.niches || []) {
      if (systemNicheModelId(system.id, niche.id) === nicheModel)
        return { system, niche };
    }
  }
  return null;
}

/** Does a niche's `nicheModel` name one of a system's niches? */
const isSystemNicheModel = (nicheModel) => !!systemNicheOfModel(nicheModel);

/**
 * The system's niche nearest a niche of `widthIn` × `heightIn` (the least
 * |Δwidth| + |Δheight|), or null when the system has none.
 */
function nearestSystemNiche(systemId, size) {
  return systemNichesByNearness(systemId, size)[0] ?? null;
}

/**
 * The system's niches nearest first to a niche of `widthIn` × `heightIn`
 * (|Δwidth| + |Δheight|; the definition's order between equals) — what is
 * tried in turn when the nearest does not fit where the niche is.
 */
function systemNichesByNearness(systemId, { widthIn, heightIn }) {
  const off = (n) =>
    Math.abs(n.widthIn - widthIn) + Math.abs(n.heightIn - heightIn);
  return [...(showerSystemById(systemId)?.niches || [])].sort(
    (a, b) => off(a) - off(b)
  );
}

/** The `nicheModel` a system niche writes on the niche: `<system>--<niche>`. */
const systemNicheModelId = (systemId, nicheId) => `${systemId}--${nicheId}`;

/**
 * A system's accessories that go in `kind` ('shower' | 'tub-shower'), in
 * the order the panel offers them; none for an unknown system.
 */
const systemAccessories = (systemId, kind) =>
  (showerSystemById(systemId)?.accessories || []).filter((a) =>
    a.appliesTo.includes(kind)
  );

/** The part id a system's bench is priced by (step 4b): `<system>--<bench>`. */
const systemBenchPartId = (system) => `${system.id}--${system.bench.id}`;

/** What an accessory of a system carries as `showerSystemPart`. */
const systemAccessoryPartId = (systemId, partId) => `${systemId}--${partId}`;

/**
 * The system and accessory a room object's `showerSystemPart` names, or
 * null — not a system part, or one no definition has.
 *
 * @returns {{system: Object, accessory: Object}|null}
 */
function systemAccessoryOf(obj) {
  const part = obj?.showerSystemPart;
  if (typeof part !== 'string') return null;
  for (const system of SHOWER_SYSTEMS) {
    const prefix = `${system.id}--`;
    if (!part.startsWith(prefix)) continue;
    const id = part.slice(prefix.length);
    const accessory = (system.accessories || []).find((a) => a.id === id);
    return accessory ? { system, accessory } : null;
  }
  return null;
}

/**
 * An accessory's size in inches as the object is sized: `x` along the wall
 * its local +Z points out of, `z` along the wall its local +X points out of
 * (a corner part's two sides; the extended seat's width along the back wall
 * and its depth), `y` its height.
 */
const accessorySizeIn = (accessory) =>
  accessory.shape === 'extended-seat'
    ? { x: accessory.widthIn, y: accessory.heightIn, z: accessory.depthIn }
    : { x: accessory.sidesIn, y: accessory.heightIn, z: accessory.sidesIn };

/** The `unifiedTextures` doc id of a choice's wall panel. */
const systemTextureId = ({ id, colour: colourId, finish }) =>
  `${id}--${colourId}--${finish}`;

/**
 * The choice a wall panel's texture id names (`systemTextureId` read back),
 * or null for a tile or a panel the definitions no longer have.
 */
function systemChoiceOfTextureId(textureId) {
  if (typeof textureId !== 'string') return null;
  const [id, colourId, finish, ...rest] = textureId.split('--');
  const choice = { id, colour: colourId, finish };
  return rest.length === 0 && isValidSystemChoice(choice) ? choice : null;
}

/** "Onyx Collection · Concrete · Gloss smooth", or null for no valid choice. */
function systemChoiceLabel(choice) {
  if (!isValidSystemChoice(choice)) return null;
  const system = showerSystemById(choice.id);
  return [
    system.name,
    system.colours.find((c) => c.id === choice.colour).name,
    system.wallFinishes.find((f) => f.id === choice.finish).name,
  ].join(' · ');
}

/**
 * What a REBUILT shower carries over from the one it replaces — its system,
 * the tile it had before it, and the prefab pan the system's base set with
 * the pan it replaced: `{ showerSystem, showerTileBeforeSystem, showerPan?,
 * showerPanBeforeSystem? }`, or `{}` for a tile shower (or a choice the
 * definitions no longer have). A relayout re-creates a shower as a fresh
 * object; without these it came back with the panels on its walls and no
 * system on it, billing floor tile again.
 */
function systemFieldsOf(obj) {
  if (!isValidSystemChoice(obj?.showerSystem)) return {};
  return {
    showerSystem: obj.showerSystem,
    showerTileBeforeSystem: obj.showerTileBeforeSystem ?? null,
    ...(obj.showerPan !== undefined ? { showerPan: obj.showerPan } : {}),
    ...(obj.showerPanBeforeSystem !== undefined
      ? { showerPanBeforeSystem: obj.showerPanBeforeSystem }
      : {}),
  };
}

// what a system's part says it is of: its maker, or the system's own name
// where it has none (a generic system, Acrylic)
const systemLabelOf = (system) => system.maker ?? system.name;

/**
 * What a room object is of a shower SYSTEM, for its price (step 2): the
 * system shower itself — its pan the system's base — or the system's niche
 * in that shower's fixture group (`nicheBuild: 'prefab'` with a
 * `nicheModel`) — or, step 3a, one of its accessories in that group (an
 * object whose `showerSystemPart` names an accessory of the shower's own
 * system): `{ brand, label, part: 'base' | 'niche' | 'accessory', id?,
 * name? }`, `brand` the maker its price-book lines narrow by (null for a
 * generic system, which no maker's line prices) and `label` the system it
 * says it is of (its maker, else its name); a niche insert's or an
 * accessory's `id` is the part itself (its `nicheModel`, its
 * `showerSystemPart`) and `name` what the maker calls it (step 5), the
 * product a line naming it prices. Null for anything else, a
 * tub/shower's tub included (it has no base; the tub is its own product),
 * and a grab bar (the standard one, of no system).
 */
function systemPartOf(obj, roomObjects) {
  if (!obj) return null;
  // a system with no base of its own (Wetwall) stands on the plain prefab
  // pan, priced as a tile shower's (step 4b)
  if (
    obj.type === 'shower' &&
    isValidSystemChoice(obj.showerSystem) &&
    systemHasBase(obj.showerSystem.id)
  ) {
    const system = showerSystemById(obj.showerSystem.id);
    return { brand: system.maker, label: systemLabelOf(system), part: 'base' };
  }
  if (
    obj.type === 'shower-niche' &&
    obj.nicheBuild === 'prefab' &&
    obj.nicheModel &&
    obj.fixtureGroupId
  ) {
    const carrier = (roomObjects || []).find(
      (o) =>
        o?.fixtureGroupId === obj.fixtureGroupId &&
        isValidSystemChoice(o.showerSystem)
    );
    if (carrier) {
      const insert = systemNicheOfModel(obj.nicheModel);
      const system = showerSystemById(carrier.showerSystem.id);
      return {
        brand: system.maker,
        label: systemLabelOf(system),
        part: 'niche',
        ...(insert
          ? { id: obj.nicheModel, name: `${insert.niche.name} Niche` }
          : {}),
      };
    }
  }
  // a bench in the group of a shower whose system makes one (step 4b)
  if (obj.type === 'shower-bench' && obj.fixtureGroupId) {
    const carrier = (roomObjects || []).find(
      (o) =>
        o?.fixtureGroupId === obj.fixtureGroupId &&
        isValidSystemChoice(o.showerSystem)
    );
    const system = carrier ? showerSystemById(carrier.showerSystem.id) : null;
    if (system?.bench) {
      return {
        brand: system.maker,
        label: systemLabelOf(system),
        part: 'bench',
        id: systemBenchPartId(system),
        name: system.bench.name,
      };
    }
    return null;
  }
  // an accessory of the system on its group's shower (step 3a)
  const accessoryOf = systemAccessoryOf(obj);
  if (accessoryOf && obj.fixtureGroupId) {
    const carrier = (roomObjects || []).find(
      (o) =>
        o?.fixtureGroupId === obj.fixtureGroupId &&
        isValidSystemChoice(o.showerSystem) &&
        o.showerSystem.id === accessoryOf.system.id
    );
    if (carrier) {
      return {
        brand: accessoryOf.system.maker,
        label: systemLabelOf(accessoryOf.system),
        part: 'accessory',
        id: obj.showerSystemPart,
        name: accessoryOf.accessory.name,
      };
    }
  }
  return null;
}

/**
 * Is `textureId` a system's panel (`systemTextureId`) rather than a tile? A
 * panel comes with choosing the system, so whatever reads "the room's shower
 * tile" — a package, a preset, an undo, the floor matched to the wall — must
 * pass over it, or it hands the panels to tile showers.
 */
const isSystemTextureId = (textureId) =>
  typeof textureId === 'string' &&
  SHOWER_SYSTEMS.some((system) => textureId.startsWith(`${system.id}--`));

/**
 * The one use a system's panel texture is tagged with: its walls are set by
 * choosing the system, so no tile picker asks for it.
 */
const SYSTEM_PANEL_APPLICATION_TYPE = 'ShowerSystemPanel';

/**
 * Is a catalog doc a system's panel texture — never offered as a finish, nor
 * a Finishes row of the Price Book (2026-10-31)? Read off the doc's tag, not
 * its id: a system's niche MODEL carries the same id prefix.
 */
const isSystemPanelTexture = (doc) =>
  Array.isArray(doc?.applicationType) &&
  doc.applicationType.includes(SYSTEM_PANEL_APPLICATION_TYPE);

/**
 * The lowest top above `shower`'s floor, in metres, of its own wall regions
 * (`source: 'shower'`, its `fixtureGroupId`) that `keep` accepts, read off
 * each wall's drawn face as `tileSpanUpWall` lays them; null when none.
 */
function lowestRegionTopM(shower, walls, keep) {
  const group = shower?.fixtureGroupId;
  if (!group || !shower.center || !shower.dimensions) return null;
  const floorY = shower.center.y - shower.dimensions.y / 2;
  let top = null;
  for (const wall of walls || []) {
    const face = wallFaceSpanY(wall);
    if (!face) continue;
    for (const r of wall.texture?.regions || []) {
      if (r?.source !== 'shower' || r.fixtureGroupId !== group) continue;
      if (!keep(r)) continue;
      const y = face.bottom + ((r.y + r.height) / 100) * face.height - floorY;
      if (top == null || y < top) top = y;
    }
  }
  return top;
}

/**
 * The top of a shower's own system panels above its floor, in metres (its
 * system regions only). Null when it has none — then no trim is drawn or
 * priced.
 */
const showerPanelTopM = (shower, walls) =>
  lowestRegionTopM(shower, walls, (r) => isSystemTextureId(r.id));

/**
 * How high a shower's WALLS run above its floor, in metres: the lowest top
 * of its own tile or system panels — what its Wall height (Standard or
 * Full) laid. Null when its walls carry none of its own.
 */
const showerWallTopM = (shower, walls) =>
  lowestRegionTopM(shower, walls, () => true);

// a glass this little over its walls is the same height (float noise)
const GLASS_OVER_WALLS_EPS_M = 0.001;

/**
 * Why `shower`'s glass may not be `glassM` tall, or null: never taller than
 * its walls (the user, 2026-10-30: "glass shouldn't be higher than the
 * tile/wet walls"). A shower whose walls carry nothing of its own has
 * nothing to be taller than.
 */
function glassHeightProblem(shower, walls, glassM) {
  const top = showerWallTopM(shower, walls);
  if (top == null || !(glassM > top + GLASS_OVER_WALLS_EPS_M)) return null;
  return `The glass can't be taller than the shower's walls (${Math.round(
    top / 0.0254
  )}"). Raise its Wall height first.`;
}

/**
 * A shower's glass never stands above its walls (the user, 2026-10-30;
 * `glassHeightProblem`): each stall in `retiled` (the showers whose walls
 * this call laid) whose glass is taller than its walls now are is lowered
 * to their top, its floor kept — a shower whose walls were not touched is
 * left as it is. Returns the room's objects — the same array when none
 * moved — and the heights it lowered them to, in whole inches, for the
 * reply.
 */
function glassUnderWalls(objects, walls, retiled) {
  const lowered = [];
  const next = (objects || []).map((o) => {
    if (o?.type !== 'shower' || !retiled.has(o.id)) return o;
    if (!o.center || !o.dimensions) return o;
    if (!glassHeightProblem(o, walls, o.dimensions.y)) return o;
    const top = showerWallTopM(o, walls);
    const floorY = o.center.y - o.dimensions.y / 2;
    lowered.push(Math.round(top / 0.0254));
    return {
      ...o,
      center: { ...o.center, y: floorY + top / 2 },
      dimensions: { ...o.dimensions, y: top },
    };
  });
  return { objects: lowered.length ? next : objects, lowered };
}

/**
 * Whether the company offers a system — only when its row says `true`
 * (`offering` is the `{ key: boolean }` map `offeringMap` builds).
 */
const isShowerSystemOffered = (offering, id) =>
  (offering || {})[showerSystemOfferingKey(id)] === true;

/** The systems a company offers that apply to `kind` ('shower' | 'tub-shower'). */
const offeredShowerSystems = (offering, kind) =>
  READY_SHOWER_SYSTEMS.filter(
    (system) =>
      isShowerSystemOffered(offering, system.id) &&
      system.appliesTo.includes(kind)
  );

// the part types a line may name (`systemPartsNamedByLine`)
const PART_TYPES = [
  'shower-niche',
  'shower-shelf',
  'corner-caddy',
  'shower-seat',
  'shower-bench',
  'grab-bar',
];
const namesAPart = (system, name) =>
  PART_TYPES.some(
    (type) => systemPartsNamedByLine(system, name, type).length > 0
  );

/**
 * The ids of the ready systems a price list's lines name — one per line, the
 * first it names (a maker before Acrylic, as the import reads it). A system
 * whose name is an ordinary word (`namedOnlyByParts`: "acrylic" is in a
 * demolition, a tub, a bare "walls") is named only by a line naming one of
 * its PARTS (final review, 2026-10-30).
 */
const systemsNamedByLines = (names) => {
  const named = new Set();
  for (const name of names || []) {
    const system = systemNamedByLine(name);
    if (!system || !isSystemReady(system)) continue;
    if (system.namedOnlyByParts && !namesAPart(system, name)) continue;
    named.add(system.id);
  }
  return READY_SHOWER_SYSTEMS.filter((system) => named.has(system.id)).map(
    (system) => system.id
  );
};

/**
 * The texture a shower's walls are tiled in: its system's, when it carries a
 * valid `showerSystem` — whatever was asked, so Auto Style, a height change
 * or a room-wide tile pick cannot drift from the system — else
 * `requestedTextureId`.
 */
const showerWallTextureId = (shower, requestedTextureId) =>
  isValidSystemChoice(shower?.showerSystem)
    ? systemTextureId(shower.showerSystem)
    : requestedTextureId;

// the parts that make a tub a tub/shower: its valve and its head
const SHOWER_VALVE_TYPES = new Set(['showerhead', 'shower-controls']);
const TUBS = new Set(['bathtub', 'tub']);

/**
 * What a system can be put on, or null: a shower stall (`type: 'shower'`, or
 * any type its category calls a shower) is 'shower'; a tub whose fixture
 * group holds a showerhead or shower controls is 'tub-shower' (the system is
 * recorded on the tub). The ONE rule — `apply_shower_tiling` finds the
 * showers it tiles by it, and the panel offers a system by it, so the panel
 * offers one exactly where the tool can set it.
 */
function showerSystemKindOf(obj, roomObjects) {
  if (!obj) return null;
  if (obj.type === 'shower' || obj.category?.shower) return 'shower';
  if (!TUBS.has(obj.type) || !obj.fixtureGroupId) return null;
  return (roomObjects || []).some(
    (o) =>
      o?.fixtureGroupId === obj.fixtureGroupId && SHOWER_VALVE_TYPES.has(o.type)
  )
    ? 'tub-shower'
    : null;
}

/**
 * The shower-niche in a shower's fixture group, or null — the niche a
 * shower's system turns into its own, and the one the panel adds or takes
 * out (`set_shower_niche`).
 */
const groupNicheOf = (objects, fixtureGroupId) =>
  (fixtureGroupId &&
    (objects || []).find(
      (o) => o?.type === 'shower-niche' && o.fixtureGroupId === fixtureGroupId
    )) ||
  null;

module.exports = {
  WINDOW_TRIM,
  WINDOW_SILL_CAP,
  systemWindowTrim,
  systemTrimPartId,
  systemLabelOf,
  isNeoAngleShower,
  systemPanSizing,
  systemHasBase,
  systemPartColourHex,
  colourFinishes,
  choiceForColour,
  SHOWER_SYSTEMS,
  READY_SHOWER_SYSTEMS,
  isSystemReady,
  showerSystemKindOf,
  groupNicheOf,
  SHOWER_SYSTEM_KEY_PREFIX,
  showerSystemById,
  showerSystemOfferingKey,
  isValidSystemChoice,
  systemChoiceProblem,
  defaultSystemChoice,
  systemTextureId,
  systemChoiceOfTextureId,
  systemChoiceLabel,
  systemPartOf,
  systemColourHex,
  systemPanelThicknessIn,
  systemCurb,
  standardBaseSize,
  systemNiche,
  systemNicheOpeningIn,
  nearestSystemNiche,
  systemNichesByNearness,
  systemNicheModelId,
  isSystemNicheModel,
  systemNamedByLine,
  systemPartsNamedByLine,
  ACCESSORY_TYPES,
  STANDARD_GRAB_BAR,
  systemAccessories,
  systemAccessoryPartId,
  systemAccessoryOf,
  accessorySizeIn,
  PLAIN_PAN,
  isShowerSystemOffered,
  isSystemTextureId,
  SYSTEM_PANEL_APPLICATION_TYPE,
  isSystemPanelTexture,
  systemFieldsOf,
  offeredShowerSystems,
  systemsNamedByLines,
  showerWallTextureId,
  systemTrims,
  systemBead,
  chosenTrims,
  showerPanelTopM,
  showerWallTopM,
  glassHeightProblem,
  glassUnderWalls,
  showerTrimRuns,
  showerTrimFeet,
};
